import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { MELOGY_WHITE, MELOGY_ALMOST_OPAQUE, MELOGY_BACKGROUND_MEDIUM_LIGHT_GREY, MELOGY_BACKGROUND_2XLIGHT_GREY } from "../../style/colors";
import { FONT_SIZE_2XS, TITLE_FONT_FAMILY, TITLE_FONT_WEIGHT } from "../../style/fontSettings";
import Button from "../buttons/Button";

const PickerContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
`;

const InputContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Label = styled.label`
    font-family: ${TITLE_FONT_FAMILY};
    font-weight: ${TITLE_FONT_WEIGHT};
    font-size: ${FONT_SIZE_2XS};
    color: ${MELOGY_WHITE};
    text-transform: uppercase;
`;

const ImageUrlInput = styled.input`
    width: 100%;
    padding: 8px;
    background-color: ${MELOGY_ALMOST_OPAQUE};
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    color: ${MELOGY_WHITE};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: rgba(255, 255, 255, 0.6);
    }
`;

const PreviewContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CategoryButtonsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
`;

const ImageGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    max-height: 500px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px;
    
    &::-webkit-scrollbar {
        width: 2px;
    }
    
    &::-webkit-scrollbar-track {
        background: ${MELOGY_BACKGROUND_MEDIUM_LIGHT_GREY};
    }
    
    &::-webkit-scrollbar-thumb {
        background: ${MELOGY_BACKGROUND_2XLIGHT_GREY};
        cursor: grab;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        cursor: grab;
    }
    
    &::-webkit-scrollbar-thumb:active {
        cursor: grabbing;
    }
`;

const ImageThumbnail = styled.div`
    width: 100%;
    aspect-ratio: 1;
    background-image: ${({ imageUrl }) => `url(${imageUrl})`};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 4px;
    border: 2px solid ${({ isSelected }) => isSelected ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        border-color: rgba(255, 255, 255, 0.6);
        transform: scale(1.05);
    }
`;

// Image categories with many more images
const imageCategories = {
    "Space & Astronomy": [
        // Unsplash space images (no attribution required)
        "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1502134249126-9f3755a50d78?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1465101162946-4377e57745c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1446776877081-d282a0f896e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1467810563316-b5476525c0f9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1516339901601-2e1b62dc0c94?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1506443432602-ac2fcd6f54e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1502134249126-9f3755a50d78?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    ],
    "ESA/Hubble": [
        // ESA/Hubble images (CC BY 4.0 - attribution required)
        "https://esahubble.org/media/archives/images/screen/heic1502a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic2007a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic1913a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic2017a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic2020a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic2001a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic2016a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic2018a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic2019a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic2021a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic1509a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic1608a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic1701a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic1801a.jpg",
        "https://esahubble.org/media/archives/images/screen/heic1901a.jpg",
    ],
    "Nature": [
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1472214103451-9374bd1c798e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1511497584788-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1447752875215-b2761acb3c5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1501594907352-04cda38ebc29?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1472214103451-9374bd1c798e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    ],
    "Abstract": [
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1557672172-298e090bd0f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    ],
};

const BackgroundImagePicker = ({ value, onChange }) => {
    const [imageUrl, setImageUrl] = useState(value || "");
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("Space & Astronomy");
    const [images, setImages] = useState(imageCategories["Space & Astronomy"] || []);

    useEffect(() => {
        setImageUrl(value || "");
        // Check if value is in any category
        const allImages = Object.values(imageCategories).flat();
        if (value && allImages.includes(value)) {
            setSelectedImage(value);
            // Find which category contains this image
            for (const [category, categoryImages] of Object.entries(imageCategories)) {
                if (categoryImages.includes(value)) {
                    setSelectedCategory(category);
                    setImages(categoryImages);
                    break;
                }
            }
        }
    }, [value]);

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setImages(imageCategories[category] || []);
        setSelectedImage(null); // Reset selection when changing category
    };

    const handleUrlChange = (e) => {
        const newUrl = e.target.value;
        setImageUrl(newUrl);
        onChange("backgroundImage", newUrl);
        const allImages = Object.values(imageCategories).flat();
        if (allImages.includes(newUrl)) {
            setSelectedImage(newUrl);
        } else {
            setSelectedImage(null);
        }
    };

    const handleImageSelect = (url) => {
        setSelectedImage(url);
        setImageUrl(url);
        onChange("backgroundImage", url);
    };

    const categoryNames = Object.keys(imageCategories);

    return (
        <PickerContainer onMouseDown={(e) => e.stopPropagation()}>
            <PreviewContainer>
                <Label>Select Image</Label>
                <CategoryButtonsContainer>
                    {categoryNames.map((category) => (
                        <Button
                            key={category}
                            text={category}
                            height="28px"
                            width="auto"
                            padding="4px 12px"
                            justifyContent="center"
                            buttonColor="secondary"
                            isSelected={selectedCategory === category}
                            onClick={() => handleCategorySelect(category)}
                        />
                    ))}
                </CategoryButtonsContainer>
                {images.length > 0 && (
                    <ImageGrid>
                        {images.map((url, index) => (
                            <ImageThumbnail
                                key={index}
                                imageUrl={url}
                                isSelected={selectedImage === url}
                                onClick={() => handleImageSelect(url)}
                            />
                        ))}
                    </ImageGrid>
                )}
            </PreviewContainer>
            
            <InputContainer>
                <Label>Or Enter Custom URL</Label>
                <ImageUrlInput
                    type="text"
                    value={imageUrl}
                    onChange={handleUrlChange}
                    placeholder="Enter custom image URL"
                />
            </InputContainer>
        </PickerContainer>
    );
};

export default BackgroundImagePicker;
